/* ============================================================
   PebbleBloom - Above The Fold
   File: assets/pb-atf.js

   Collects the product benefit blocks and lifts them above the
   title as a badge ticker.

   Why JS and not CSS or Liquid:
   - .product__info-container is display:block, so CSS order is
     unavailable, and switching it to flex to gain order would
     change margin collapsing across the whole buy box.
   - The alternative is editing main-product.liquid, which is
     11,685 lines and drives every product page on the store.

   This is purely additive. Without the script the benefits stay
   exactly where the theme put them and still read correctly.
   ============================================================ */

(function () {
  'use strict';

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function build(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;

    Array.prototype.forEach.call(
      root.querySelectorAll('.product__info-container'),
      function (info) {
        if (info.dataset.pbAtfInit === '1') return;

        var feats = info.querySelectorAll('[class*="qa-feature-"]');
        var title = info.querySelector('.product__title');
        if (!feats.length || !title) return;

        info.dataset.pbAtfInit = '1';

        var ticker = document.createElement('div');
        ticker.className = 'pb-atf__ticker';

        var track = document.createElement('div');
        track.className = 'pb-atf__track';
        ticker.appendChild(track);

        Array.prototype.forEach.call(feats, function (f, i) {
          var pill = document.createElement('span');
          pill.className = 'pb-atf__pill';
          pill.style.setProperty('--i', i);

          var img = f.querySelector('img');
          if (img) {
            var ic = document.createElement('span');
            ic.className = 'pb-atf__ico';
            var clone = img.cloneNode(true);
            clone.removeAttribute('width');
            clone.removeAttribute('height');
            clone.setAttribute('loading', 'lazy');
            clone.setAttribute('alt', '');
            ic.appendChild(clone);
            pill.appendChild(ic);
          }

          var label = document.createElement('span');
          label.className = 'pb-atf__label';
          label.textContent = (f.textContent || '').trim();
          pill.appendChild(label);

          track.appendChild(pill);
          f.classList.add('pb-atf__source-hidden');
        });

        // Duplicate the set so the marquee can loop without a gap.
        // aria-hidden because it is the same text read twice.
        if (!reduced) {
          var dupe = track.cloneNode(true);
          dupe.className = 'pb-atf__track pb-atf__track--dupe';
          dupe.setAttribute('aria-hidden', 'true');
          ticker.appendChild(dupe);
          ticker.classList.add('pb-atf__ticker--marquee');
        }

        // Top of the buy box, above the social-proof row - not just
        // above the title. The badges are a summary of the product,
        // so they should be the first thing read in this column.
        info.insertBefore(ticker, info.firstElementChild);
      }
    );
  }

  /* The Benefit Cards section renders as a normal full-width section
     after the buy box, which on desktop puts it below the gallery
     where nobody scrolls past the fold to find it. A copy of the grid
     goes into the product column under the price, and the original
     section is hidden.

     Copied rather than moved. The theme editor re-renders a section on
     every settings change, and a grid that had been moved out lives
     outside that section, so it survived the re-render - leaving a
     stale set of cards in the buy box and a fresh set back in the
     section's own position further down the page. That is the "it
     jumps down when I edit it" behaviour. Moving also meant that
     re-rendering the *product* section destroyed the relocated grid
     outright, with nothing left to recover it from.

     Copying makes this idempotent: the section always holds the
     authoritative grid, and each pass simply rebuilds the copy from
     it. The section stays a real, editable section - only a mirror of
     its output moves. */
  function syncBenefitCards() {
    var info = document.querySelector('.product__info-container');
    if (!info) return;

    // The source is the grid still sitting inside its own section.
    var source = null;
    var grids = document.querySelectorAll('.pb-bc__grid');
    for (var i = 0; i < grids.length; i++) {
      if (!grids[i].classList.contains('pb-bc__grid--inline')) {
        source = grids[i];
        break;
      }
    }
    if (!source) return;

    var anchor =
      info.querySelector('[class*="pb-price-block-"]') ||
      info.querySelector('.price') ||
      info.querySelector('.product-form__input');
    if (!anchor) return;

    var copy = source.cloneNode(true);
    copy.classList.add('pb-bc__grid--inline');

    /* Editor handles stay on the original only. Two elements claiming
       the same block id makes selecting a card in the editor
       ambiguous. */
    copy.removeAttribute('data-shopify-editor-block');
    Array.prototype.forEach.call(
      copy.querySelectorAll('[data-shopify-editor-block]'),
      function (n) {
        n.removeAttribute('data-shopify-editor-block');
      }
    );
    // Cloned from an initialised grid, so clear the flag or the shine
    // cycle would consider this copy already running and skip it.
    copy.removeAttribute('data-pb-shine-init');

    var existing = info.querySelector('.pb-bc__grid--inline');
    if (existing) existing.remove();
    anchor.parentNode.insertBefore(copy, anchor.nextSibling);

    var host = source.closest('.shopify-section');
    if (host) host.style.display = 'none';

    // Lets pb-benefit-cards.js pick up the new copy. A dedicated event
    // rather than a synthetic shopify:section:load, which this file
    // also listens for and would loop on.
    document.dispatchEvent(
      new CustomEvent('pb:content-changed', { detail: { scope: info } })
    );
  }

  function run(scope) {
    build(scope);
    syncBenefitCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      run();
    });
  } else {
    run();
  }

  document.addEventListener('shopify:section:load', function (e) {
    run(e.target);
  });
})();
